import { NgModule } from '@angular/core';
import { RouterModule } from '@angular/router';
import { FeatureFlag } from '@app/enums';
import { AdminGuard, FeatureGuard } from '@app/guards';
import { RoutesWithOptions } from '@app/interfaces';
import { AggregatedDataSurveyComponent, RawDataSurveyComponent } from '@performance/components';
import { PerformanceResolver } from '@performance/performance.resolver';
import {
    BulkReviewStoreView,
    FeedbackIndexView,
    FeedbackShowView,
    GoalIndexView,
    GoalShowView,
    GoalStoreView,
    OverviewShowView,
    ReviewIndexView,
    ReviewShowView,
    ReviewStoreView,
    SurveyIndexView,
    SurveyShowView,
    SurveyStoreView,
    TemplateIndexView,
    TemplateShowView,
} from '@performance/views';

const routes: RoutesWithOptions = [
    {
        path: '',
        redirectTo: 'overview',
        pathMatch: 'full',
    },
    {
        path: 'overview',
        component: OverviewShowView,
        canActivate: [AdminGuard],
    },
    {
        path: 'team/:employee/feedback/:feedback',
        redirectTo: 'feedback/:feedback',
    },
    {
        path: 'feedback',
        children: [
            {
                path: '',
                component: FeedbackIndexView,
                canActivate: [AdminGuard],
            },
            {
                path: ':feedback',
                component: FeedbackShowView,
                resolve: {
                    models: PerformanceResolver,
                },
            },
        ],
    },

    {
        path: 'goals',
        children: [
            {
                path: '',
                component: GoalIndexView,
                canActivate: [AdminGuard],
            },
            {
                path: 'create',
                component: GoalStoreView,
            },
            {
                path: ':goal',
                component: GoalShowView,
                resolve: {
                    models: PerformanceResolver,
                },
            },
        ],
    },

    {
        path: 'reviews',
        children: [
            {
                path: '',
                component: ReviewIndexView,
                canActivate: [AdminGuard],
            },
            {
                path: 'create',
                component: ReviewStoreView,
            },
            {
                path: 'create-bulk',
                component: BulkReviewStoreView,
                canActivate: [FeatureGuard],
                data: {
                    feature: {
                        flags: [FeatureFlag.performanceReviewImprovements],
                    },
                },
            },
            {
                path: ':review',
                component: ReviewShowView,
                resolve: {
                    models: PerformanceResolver,
                },
            },
        ],
    },

    /**
     * Legacy redirects until surveys broken out into own module
     */
    {
        path: 'surveys/:id',
        redirectTo: '/surveys/center/:id',
    },
    {
        path: 'surveys',
        redirectTo: '/surveys/center',
    },

    {
        path: 'center',
        children: [
            {
                path: '',
                component: SurveyIndexView,
            },
            {
                path: 'create',
                component: SurveyStoreView,
            },
            {
                path: 'templates',
                children: [
                    {
                        path: '',
                        component: TemplateIndexView,
                    },
                    {
                        path: ':template',
                        component: TemplateShowView,
                        resolve: {
                            models: PerformanceResolver,
                        },
                    },
                ],
            },
            {
                path: ':survey',
                resolve: {
                    models: PerformanceResolver,
                },
                children: [
                    {
                        path: '',
                        component: SurveyShowView,
                    },
                    {
                        path: 'aggregated-data',
                        component: AggregatedDataSurveyComponent,
                    },
                    {
                        path: 'raw-data',
                        component: RawDataSurveyComponent,
                    },
                ],
            },
        ],
    },

    {
        path: 'templates',
        children: [
            {
                path: '',
                component: TemplateIndexView,
                canActivate: [AdminGuard],
            },
            {
                path: ':template',
                component: TemplateShowView,
                resolve: {
                    models: PerformanceResolver,
                },
            },
        ],
    },
];

@NgModule({
    imports: [RouterModule.forChild(routes)],
    exports: [RouterModule],
})
export class PerformanceRouterModule {}
